// Phase OP — slice 6. Read-only hook over odds_health_snapshot().
// Founder-only at the DB layer (the RPC raises 42501 for non-admins);
// the page is also wrapped in <AdminOnly> at the route. Honest-empty
// soft-fail: a non-admin / errored call returns the EMPTY snapshot,
// never fabricated counters.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export interface OddsHealthCredits {
  remaining: number | null;
  used: number | null;
  last_seen_at: string | null;
  budget_blocks_24h: number;
  floor: number;
  warn_threshold: number;
}
export interface OddsHealthLedgerRow {
  ran_at: string;
  call_status: string;
  endpoint: string;
  sport_key: string | null;
  http_status: number | null;
  requests_remaining: number | null;
  notes: string | null;
}
export interface OddsHealthEscalation {
  id: string;
  reason: string;
  severity: string;
  endpoint: string | null;
  sport_key: string | null;
  raised_at: string;
  notes: string | null;
}
export interface OddsHealthSnapshot {
  as_of: string | null;
  credits: OddsHealthCredits;
  ledger_recent: OddsHealthLedgerRow[];
  escalations_open: OddsHealthEscalation[];
  escalations_24h_count: number;
  fixture_markets: { rows: number; fixtures: number; history_rows: number };
  benchmarks: {
    pinnacle_rows: number;
    closing_lines_rows: number;
    closing_lines_current: number;
  };
  sport_coverage: {
    live_markets: number;
    calibrating_leagues: number;
    live_leagues: number;
  };
  next_kickoffs: { fixture_id: string; kickoff_at: string }[];
}

const EMPTY: OddsHealthSnapshot = {
  as_of: null,
  credits: {
    remaining: null,
    used: null,
    last_seen_at: null,
    budget_blocks_24h: 0,
    floor: 25,
    warn_threshold: 100,
  },
  ledger_recent: [],
  escalations_open: [],
  escalations_24h_count: 0,
  fixture_markets: { rows: 0, fixtures: 0, history_rows: 0 },
  benchmarks: { pinnacle_rows: 0, closing_lines_rows: 0, closing_lines_current: 0 },
  sport_coverage: { live_markets: 0, calibrating_leagues: 0, live_leagues: 0 },
  next_kickoffs: [],
};

export interface UseOddsHealthState {
  readonly snapshot: OddsHealthSnapshot;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => Promise<void>;
}

export function useOddsHealth(): UseOddsHealthState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [snapshot, setSnapshot] = useState<OddsHealthSnapshot>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await (supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: unknown; error: { message: string } | null }>)(
        "odds_health_snapshot",
      );
      if (rpcErr) {
        setError(rpcErr.message);
        setSnapshot(EMPTY);
      } else if (data && typeof data === "object") {
        setSnapshot({ ...EMPTY, ...(data as Partial<OddsHealthSnapshot>) });
      } else {
        setSnapshot(EMPTY);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSnapshot(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({ snapshot, loading, error, refetch }),
    [snapshot, loading, error, refetch],
  );
}
